import { Chess } from 'chess.js';
import type { EvalScore } from './chessEval';
import { uciToMove } from './chessStockfish';

export type MoveReviewLabel =
  | 'best'
  | 'excellent'
  | 'good'
  | 'inaccuracy'
  | 'mistake'
  | 'blunder';

export type MoveReviewResult = {
  label: MoveReviewLabel;
  centipawnLoss: number;
  playedUci: string;
  bestUci: string | null;
  matchedBest: boolean;
  summary: string;
};

/** UCI from chess.js verbose move. */
/** Human-readable SAN for a UCI best-move suggestion (from fenBefore, your turn). */
export function uciBestMoveSan(fen: string, uci: string | null): string | null {
  if (!uci || uci === '(none)') return null;
  try {
    const chess = new Chess(fen);
    const { from, to, promotion } = uciToMove(uci);
    const m = chess.move(promotion ? { from, to, promotion } : { from, to });
    return m?.san ?? uci;
  } catch {
    return uci;
  }
}

export function verboseMoveToUci(move: {
  from: string;
  to: string;
  promotion?: string;
}): string {
  return `${move.from}${move.to}${move.promotion ?? ''}`;
}

/** Centipawns from the moving side's perspective (higher = better for mover). */
export function evalCentipawnsForMover(score: EvalScore, mover: 'w' | 'b'): number | null {
  if (score.kind === 'cp') {
    return mover === 'w' ? score.value : -score.value;
  }
  const m = score.value;
  if (mover === 'w') {
    if (m > 0) return 10_000 - m * 100;
    if (m < 0) return -10_000 - m * 100;
    return 0;
  }
  if (m < 0) return 10_000 + m * 100;
  if (m > 0) return -10_000 + m * 100;
  return 0;
}

/** Centipawn loss for the side that just moved (positive = worse). */
export function centipawnLossForMover(
  evalBefore: EvalScore,
  evalAfter: EvalScore,
  mover: 'w' | 'b'
): number | null {
  const before = evalCentipawnsForMover(evalBefore, mover);
  const after = evalCentipawnsForMover(evalAfter, mover);
  if (before === null || after === null) return null;
  return Math.max(0, before - after);
}

export function classifyCentipawnLoss(cpLoss: number, matchedBest: boolean): MoveReviewLabel {
  if (matchedBest || cpLoss <= 10) return 'best';
  if (cpLoss <= 25) return 'excellent';
  if (cpLoss <= 50) return 'good';
  if (cpLoss <= 100) return 'inaccuracy';
  if (cpLoss <= 200) return 'mistake';
  return 'blunder';
}

export function moveReviewSummary(label: MoveReviewLabel, cpLoss: number, matchedBest: boolean): string {
  if (matchedBest) return 'Best move — engine agrees.';
  const pawn = (cpLoss / 100).toFixed(1);
  switch (label) {
    case 'best':
      return `Best move (within ${pawn} pawns of engine).`;
    case 'excellent':
      return `Excellent — about ${pawn} pawn off the engine.`;
    case 'good':
      return `Good move — small difference (~${pawn} pawn).`;
    case 'inaccuracy':
      return `Inaccuracy — you gave up about ${pawn} pawn(s).`;
    case 'mistake':
      return `Mistake — roughly ${pawn} pawn(s) worse than the engine line.`;
    case 'blunder':
      return `Blunder — about ${pawn} pawn(s) lost vs the engine.`;
  }
}

export function buildMoveReviewResult(opts: {
  evalBefore: EvalScore;
  evalAfter: EvalScore;
  mover: 'w' | 'b';
  playedUci: string;
  bestUci: string | null;
}): MoveReviewResult | null {
  const cpLoss = centipawnLossForMover(opts.evalBefore, opts.evalAfter, opts.mover);
  if (cpLoss === null) return null;

  const best = opts.bestUci?.replace(/\s/g, '').toLowerCase() ?? '';
  const played = opts.playedUci.replace(/\s/g, '').toLowerCase();
  const matchedBest = Boolean(best && played === best);

  const label = classifyCentipawnLoss(cpLoss, matchedBest);
  return {
    label,
    centipawnLoss: cpLoss,
    playedUci: opts.playedUci,
    bestUci: opts.bestUci,
    matchedBest,
    summary: moveReviewSummary(label, cpLoss, matchedBest),
  };
}

export function moveReviewLabelText(label: MoveReviewLabel): string {
  switch (label) {
    case 'best':
      return 'Best move';
    case 'excellent':
      return 'Excellent';
    case 'good':
      return 'Good';
    case 'inaccuracy':
      return 'Inaccuracy';
    case 'mistake':
      return 'Mistake';
    case 'blunder':
      return 'Blunder';
  }
}
